import React from 'react';
import { BusinessUnit, ReportType, Series, Roles } from '@cambridgeassessment/lib-ee-commons';
import { GetReportButton } from '../ReportList.styles';
import DotsLoader from '../../DotsLoader/DotsLoaderFC';
import { Report } from '../../../types/Series';
import { Authorized } from 'lib-browser';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

interface Props {
  type: ReportType;
  report: Report;
  index: number;
  businessUnitId: BusinessUnit['id'];
  series: {
    id: Series['id'];
    name: Series['name'];
  };
  isBeingGenerated: boolean;
  onClick: (
    businessUnit: string,
    series: { id: Series['id']; name: Series['name'] },
    type: ReportType,
    index: number,
  ) => void;
  reportLinkText?: string;
  hideLink?: boolean;
}

const getReportTitle = (report: Report, type: string): string => {
  const submittedModerated = 'All moderated submissions for ';
  const submittedExamined = 'All examined submissions for ';
  const notSubmittedModerated = 'Centres with unsubmitted moderated work for ';
  const notSubmittedExamined = 'Centres with unsubmitted examined work for ';
  const rmaErrors = 'All RM file submission failures for ';
  const training = 'Training report for ';
  let reportTitle;
  switch (type) {
    case ReportType.TRAINING:
      reportTitle = `${training} ${report.series.name} series`;
      break;
    case ReportType.SUBMITTED_MODERATED:
      reportTitle = `${submittedModerated} ${report.series.name} series`;
      break;
    case ReportType.NOT_SUBMITTED_MODERATED:
      reportTitle = `${notSubmittedModerated} ${report.series.name} series`;
      break;
    case ReportType.SUBMITTED_EXAMINED:
      reportTitle = `${submittedExamined} ${report.series.name} series`;
      break;
    case ReportType.NOT_SUBMITTED_EXAMINED:
      reportTitle = `${notSubmittedExamined} ${report.series.name} series`;
      break;
    case ReportType.RMA_ERRORS:
      reportTitle = `${rmaErrors} ${report.series.name} series`;
      break;
    default:
      reportTitle = '';
      break;
  }

  return reportTitle;
};

const ReportFC: React.FC<Props> = ({
  businessUnitId,
  series,
  type,
  report,
  index,
  onClick,
  isBeingGenerated,
  reportLinkText = 'Download CSV',
  hideLink = false,
}) => {
  const selectedRole = useSelector((state: RootState) => state.userData.selectedRole);

  const onClickHandler = (e: React.MouseEvent): void => {
    e.preventDefault();
    onClick(businessUnitId, series, type, index);
  };

  return (
    <>
      <span data-qa-id={`report-title-${type.toLowerCase()}-${series.id}`}>{getReportTitle(report, type)}</span>
      {isBeingGenerated ? (
        <DotsLoader rightSide />
      ) : (
        <Authorized
          selectedRole={selectedRole}
          roles={[
            Roles.SUBMIT_SUPER_ADMIN,
            Roles.SUBMIT_ADMINISTRATOR,
            Roles.SUBMIT_COMPLIANCE,
            Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER,
          ]}
        >
          {!hideLink && (
            <GetReportButton onClick={onClickHandler} data-qa-id={`get-report-${type.toLowerCase()}-${series.id}`}>
              {reportLinkText}
            </GetReportButton>
          )}
        </Authorized>
      )}
    </>
  );
};

export default ReportFC;
